"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaChartLine,
  FaClock,
  FaExclamationTriangle,
  FaSync,
  FaTicketAlt,
  FaUsers,
} from "react-icons/fa";
import { getStaff, getTicketCountsByClientId } from "../../services/AdminService";
import { useAuth } from "../../context/AuthProvider";
import KPIWidget from "./components/dashboard/KPIWidget";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import InsightsPanel from "./components/dashboard/InsightsPanel";
import TicketChart from "./components/dashboard/TicketChart";

const readStoredValue = (key) => sessionStorage.getItem(key) || localStorage.getItem(key) || "";
const pct = (value) => `${Number(value || 0).toFixed(1)}%`;

const COLORS = {
  Open: "#2563eb",
  Inprogress: "#d97706",
  Resolved: "#16a34a",
  Noresponse: "#dc2626",
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    statusCounts: { Total: 0, Open: 0, Inprogress: 0, Resolved: 0, Noresponse: 0 },
    staffCount: 0,
    lastUpdated: null,
  });

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = readStoredValue("clientId") || user?.clientId;
      const [ticketRes, staffRes] = await Promise.all([
        getTicketCountsByClientId(clientId),
        getStaff().catch(() => ({ data: {} })),
      ]);

      const ticketData = ticketRes?.data || {};
      const staffData = Object.values(staffRes?.data || {});

      setDashboardData({
        statusCounts: {
          Total: ticketData.totalCount || 0,
          Open: ticketData.openCount || 0,
          Inprogress: ticketData.inProgressCount || 0,
          Resolved: ticketData.resolvedCount || 0,
          Noresponse: ticketData.noResponseCount || 0,
        },
        staffCount: staffData.length,
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [user?.clientId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const metrics = useMemo(() => {
    const { Total, Open, Inprogress, Resolved, Noresponse } = dashboardData.statusCounts;
    const activeTickets = Open + Inprogress;
    const resolutionRate = Total > 0 ? (Resolved / Total) * 100 : 0;
    const pendingRate = Total > 0 ? (Noresponse / Total) * 100 : 0;
    const slaRisk = pendingRate >= 10 || activeTickets >= 25 ? "High" : pendingRate >= 5 || activeTickets >= 12 ? "Medium" : "Low";

    return {
      total: Total,
      activeTickets,
      resolutionRate,
      pendingRate,
      slaRisk,
    };
  }, [dashboardData]);

  const kpis = useMemo(
    () => [
      {
        label: "Total Tickets",
        value: dashboardData.statusCounts.Total,
        hint: "All support records",
        icon: <FaTicketAlt />,
        tone: "primary",
      },
      {
        label: "Active Tickets",
        value: metrics.activeTickets,
        hint: "Open + in progress",
        icon: <FaClock />,
        tone: "warning",
      },
      {
        label: "Pending Response",
        value: metrics.pendingRate > 0 ? dashboardData.statusCounts.Noresponse : 0,
        hint: "Critical queue pressure",
        icon: <FaExclamationTriangle />,
        tone: "danger",
      },
      {
        label: "Resolution Rate",
        value: pct(metrics.resolutionRate),
        hint: "Closed successfully",
        icon: <FaChartLine />,
        tone: "success",
      },
    ],
    [dashboardData.statusCounts, metrics.activeTickets, metrics.pendingRate, metrics.resolutionRate],
  );

  const chartData = useMemo(
    () => [
      { name: "Open", count: dashboardData.statusCounts.Open, color: COLORS.Open },
      { name: "In Progress", count: dashboardData.statusCounts.Inprogress, color: COLORS.Inprogress },
      { name: "Resolved", count: dashboardData.statusCounts.Resolved, color: COLORS.Resolved },
      { name: "No Response", count: dashboardData.statusCounts.Noresponse, color: COLORS.Noresponse },
    ],
    [dashboardData.statusCounts],
  );

  const insightItems = useMemo(
    () => [
      {
        title: "High pending tickets",
        value: dashboardData.statusCounts.Noresponse > 0 ? "Alert" : "Clear",
        description:
          dashboardData.statusCounts.Noresponse > 0
            ? "There are tickets waiting on customer response."
            : "No pending customer response right now.",
        tone: dashboardData.statusCounts.Noresponse > 0 ? "danger" : "success",
      },
      {
        title: "SLA risk",
        value: metrics.slaRisk,
        description:
          metrics.slaRisk === "High"
            ? "Queue pressure is high and attention is needed."
            : metrics.slaRisk === "Medium"
              ? "Monitor the queue and balance assignments."
              : "Current queue risk is low.",
        tone: metrics.slaRisk === "High" ? "danger" : metrics.slaRisk === "Medium" ? "warning" : "success",
      },
      {
        title: "Customer queue status",
        value: dashboardData.statusCounts.Noresponse === 0 && metrics.activeTickets < 12 ? "Healthy" : "Needs review",
        description:
          dashboardData.statusCounts.Noresponse === 0 && metrics.activeTickets < 12
            ? "Queue movement is within a healthy range."
            : "Review tickets to keep response times under control.",
        tone: dashboardData.statusCounts.Noresponse === 0 && metrics.activeTickets < 12 ? "success" : "warning",
      },
    ],
    [dashboardData.statusCounts.Noresponse, metrics.activeTickets, metrics.slaRisk],
  );

  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    loadDashboard();
  }, [isRefreshing, loadDashboard]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="dash-skeleton dash-skeleton--hero" />
        <div className="row g-3 mt-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="col-12 col-sm-6 col-xl-3">
              <div className="dash-skeleton dash-skeleton--card" />
            </div>
          ))}
        </div>
        <div className="row g-3 mt-3">
          <div className="col-12 col-xl-8">
            <div className="dash-skeleton dash-skeleton--panel" />
          </div>
          <div className="col-12 col-xl-4">
            <div className="dash-skeleton dash-skeleton--panel" />
          </div>
        </div>
        <style>{styles}</style>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dashboard-hero">
        <div>
          <div className="dashboard-eyebrow">Admin Workspace</div>
          <h1 className="dashboard-title">Admin Dashboard</h1>
          <p className="dashboard-subtitle mb-0">
            Focused view of ticket volume, queue pressure, and response health.
          </p>
        </div>

        <div className="dashboard-hero__meta">
          <div className="dashboard-meta-chip">
            <FaUsers />
            <span>{dashboardData.staffCount} staff</span>
          </div>
          <button type="button" className="dashboard-btn" onClick={handleRefresh}>
            <FaSync />
            Refresh
          </button>
          <div className="dashboard-last-updated">
            {dashboardData.lastUpdated ? `Updated ${dashboardData.lastUpdated.toLocaleTimeString()}` : "Not updated yet"}
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {kpis.map((item) => (
          <div key={item.label} className="col-12 col-sm-6 col-xl-3">
            <KPIWidget {...item} />
          </div>
        ))}
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-8">
          <TicketChart
            subtitle="Ticket overview"
            title="Support distribution"
            action={<span className="dashboard-chip">Open / In Progress / Resolved / No Response</span>}
            data={chartData}
            emptyState={
              <div className="dashboard-empty">
                <FaTicketAlt className="dashboard-empty__icon" />
                <h5 className="mb-2">No ticket data yet</h5>
                <p className="dashboard-muted mb-0">The chart will render once support records are available.</p>
              </div>
            }
          />
        </div>

        <div className="col-12 col-xl-4">
          <InsightsPanel subtitle="Insights" title="Actionable signals" items={insightItems} />
        </div>
      </div>

      <style>{styles}</style>
    </DashboardLayout>
  );
}

const styles = `
  .dashboard-shell{
    padding:24px;
    min-height:100%;
    background:
      radial-gradient(circle at top right,rgba(37,99,235,.08),transparent 24%),
      radial-gradient(circle at left center,rgba(22,163,74,.05),transparent 22%),
      linear-gradient(180deg,#f5f7fb 0%,#edf2f7 100%);
  }

  .dashboard-hero{
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    gap:16px;
    margin-bottom:18px;
    padding:24px;
    border-radius:24px;
    background:#fff;
    border:1px solid rgba(148,163,184,.18);
    box-shadow:0 18px 40px rgba(15,23,42,.06);
  }

  .dashboard-eyebrow,.dashboard-label,.dash-card__eyebrow{
    text-transform:uppercase;
    letter-spacing:.08em;
    font-size:.72rem;
    font-weight:700;
    color:#6b7280;
  }

  .dashboard-title{
    margin:0;
    font-size:clamp(1.8rem,2.6vw,2.45rem);
    font-weight:800;
    letter-spacing:-.03em;
    color:#0f172a;
  }

  .dashboard-subtitle{
    margin-top:8px;
    color:#556175;
    max-width:720px;
    line-height:1.6;
  }

  .dashboard-hero__meta{
    display:flex;
    flex-wrap:wrap;
    gap:10px;
    justify-content:flex-end;
    align-content:flex-start;
  }

  .dashboard-meta-chip,.dashboard-btn,.dashboard-chip,.dashboard-last-updated{
    display:inline-flex;
    align-items:center;
    gap:8px;
    border-radius:999px;
    font-weight:700;
  }

  .dashboard-meta-chip{
    padding:9px 14px;
    background:#f8fafc;
    color:#334155;
    border:1px solid #e2e8f0;
  }

  .dashboard-last-updated{
    padding:9px 14px;
    background:#eef4fb;
    color:#35507a;
    border:1px solid #dbe4f0;
    font-size:.82rem;
  }

  .dashboard-btn{
    padding:9px 14px;
    border:1px solid #dbe4f0;
    background:#fff;
    color:#0f172a;
    transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease;
  }

  .dashboard-btn:hover{
    transform:translateY(-1px);
    border-color:#cbd5e1;
    box-shadow:0 10px 24px rgba(15,23,42,.08);
  }

  .dash-card{
    padding:20px;
    border-radius:22px;
    background:rgba(255,255,255,.92);
    border:1px solid rgba(148,163,184,.18);
    box-shadow:0 14px 34px rgba(15,23,42,.06);
  }

  .dash-card__header{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:12px;
    margin-bottom:16px;
  }

  .dash-card__title{
    font-size:1.08rem;
    font-weight:800;
    color:#0f172a;
    letter-spacing:-.02em;
  }

  .dashboard-chip{
    padding:6px 12px;
    background:#eef4fb;
    color:#35507a;
    font-size:.8rem;
  }

  .kpi-widget{
    padding:18px;
    border-radius:22px;
    background:#fff;
    border:1px solid rgba(148,163,184,.18);
    box-shadow:0 14px 30px rgba(15,23,42,.05);
    transition:transform .18s ease,box-shadow .18s ease;
  }

  .kpi-widget:hover{
    transform:translateY(-2px);
    box-shadow:0 18px 36px rgba(15,23,42,.08);
  }

  .kpi-widget__top{
    display:flex;
    justify-content:space-between;
    gap:16px;
  }

  .kpi-widget__label{
    color:#64748b;
    font-size:.78rem;
    font-weight:800;
    letter-spacing:.08em;
    text-transform:uppercase;
  }

  .kpi-widget__value{
    margin-top:8px;
    font-size:2rem;
    line-height:1.05;
    font-weight:800;
    color:#0f172a;
  }

  .kpi-widget__icon{
    width:48px;
    height:48px;
    border-radius:16px;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    flex-shrink:0;
    font-size:1.1rem;
  }

  .kpi-widget__icon--primary{background:#dbeafe;color:#2563eb}
  .kpi-widget__icon--success{background:#dcfce7;color:#16a34a}
  .kpi-widget__icon--warning{background:#ffedd5;color:#d97706}
  .kpi-widget__icon--danger{background:#fee2e2;color:#dc2626}

  .kpi-widget__bottom{
    display:flex;
    justify-content:space-between;
    gap:12px;
    align-items:center;
    margin-top:14px;
  }

  .kpi-widget__hint{
    color:#64748b;
    font-size:.92rem;
  }

  .dashboard-muted{
    color:#64748b;
    font-size:.92rem;
    line-height:1.55;
  }

  .insight-stack{
    display:grid;
    gap:12px;
  }

  .insight-item{
    padding:16px;
    border-radius:18px;
    border:1px solid #e8edf5;
    background:#f8fafc;
  }

  .insight-item--success{background:#ecfdf5;border-color:#d1fae5}
  .insight-item--warning{background:#fff7ed;border-color:#fed7aa}
  .insight-item--danger{background:#fee2e2;border-color:#fecaca}
  .insight-item--neutral{background:#f8fafc;border-color:#e8edf5}

  .insight-item__top{
    display:flex;
    justify-content:space-between;
    gap:12px;
    align-items:flex-start;
    margin-bottom:8px;
  }

  .insight-item__title{
    font-weight:800;
    color:#162033;
  }

  .insight-item__value{
    font-size:1.35rem;
    line-height:1;
    font-weight:800;
    color:#0f172a;
  }

  .dashboard-empty{
    min-height:320px;
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    text-align:center;
    padding:24px;
  }

  .dashboard-empty__icon{
    font-size:2rem;
    color:#94a3b8;
    margin-bottom:14px;
  }

  .dash-skeleton{
    border-radius:22px;
    background:linear-gradient(90deg,#eef2f7 25%,#e4e9f1 37%,#eef2f7 63%);
    background-size:400% 100%;
    animation:dash-shimmer 1.4s ease infinite;
  }

  .dash-skeleton--hero{height:168px}
  .dash-skeleton--card{height:132px}
  .dash-skeleton--panel{height:420px}

  @keyframes dash-shimmer{
    0%{background-position:100% 0}
    100%{background-position:-100% 0}
  }

  @media (max-width: 991.98px){
    .dashboard-hero{
      grid-template-columns:1fr;
    }

    .dashboard-hero__meta{
      justify-content:flex-start;
    }
  }

  @media (max-width: 767.98px){
    .dashboard-shell{
      padding:14px;
    }

    .dashboard-hero,.dash-card,.kpi-widget{
      border-radius:18px;
    }

    .dashboard-hero{
      padding:18px;
    }

    .dashboard-title{
      font-size:1.9rem;
    }

    .kpi-widget__value{
      font-size:1.7rem;
    }
  }
`;
